import React, { useContext, useEffect, useState } from "react";
import { Progress, Spin } from "antd";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../Constants";
import AppContext from "../../AppContext";

// Read-only view of a company's subscription, for company admins. Plans are managed by the
// Super Admin only, so there is nothing to choose or save here.
const ENDS_SOON_DAYS = 30;

const statusStyles = {
  active: "bg-green-100 text-green-800",
  trial: "bg-yellow-100 text-yellow-800",
  expired: "bg-red-100 text-red-800",
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "—");

// The tier the company pays for: the smallest one that covers its employee limit.
const findTier = (plan, maxEmployees) => {
  const tiers = [...(plan?.pricingTiers || [])].sort((a, b) => a.maxEmployees - b.maxEmployees);
  return tiers.find((tier) => tier.maxEmployees >= (maxEmployees || 0)) || tiers[tiers.length - 1];
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <span className="text-gray-600">{label}</span>
    <span className="font-medium text-gray-900 text-end">{children}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="pt-5 first:pt-0">
    <h4 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">{title}</h4>
    <div className="divide-y divide-gray-100">{children}</div>
  </div>
);

const Usage = ({ label, used, max }) => {
  const percent = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const status = percent >= 100 ? "exception" : "normal";
  return (
    <div className="py-2">
      <div className="flex items-center justify-between">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900" dir="ltr">
          {used} / {max}
        </span>
      </div>
      <Progress percent={percent} status={status} showInfo={false} size="small"
        strokeColor={percent >= 80 && percent < 100 ? "#f59e0b" : undefined} />
    </div>
  );
};

const CurrentSubscriptionCard = ({ companyId }) => {
  const { t } = useTranslation();
  const { currency } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { plan, subscription }
  const [employeeUsage, setEmployeeUsage] = useState(null);
  const [subCompanyUsage, setSubCompanyUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      // Each request is independent: a failing usage call must not hide the plan.
      const [planRes, employeesRes, subCompaniesRes] = await Promise.allSettled([
        axios.get(`${BASE_URL}/Subscription/my-plan`),
        axios.get(`${BASE_URL}/Employee/organization-usage?companyId=${companyId}`),
        axios.get(`${BASE_URL}/Company/sub-company-usage?companyId=${companyId}`),
      ]);
      if (cancelled) return;
      setData(planRes.status === "fulfilled" && planRes.value.data ? planRes.value.data : null);
      setEmployeeUsage(employeesRes.status === "fulfilled" ? employeesRes.value.data : null);
      setSubCompanyUsage(subCompaniesRes.status === "fulfilled" ? subCompaniesRes.value.data : null);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    );
  }

  const plan = data?.plan;
  const subscription = data?.subscription;
  if (!plan || !subscription) {
    return (
      <div className="max-w-2xl p-8 border rounded-xl bg-white shadow-md text-gray-600">
        {t("subscription.selector.noPlan")}
      </div>
    );
  }

  const tier = findTier(plan, subscription.maxEmployees);
  const isYearly = subscription.selectedDurationType === "Yearly";
  const status = (subscription.status || "").toLowerCase();
  const daysLeft = subscription.endDate
    ? Math.ceil((new Date(subscription.endDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const endDateClass =
    status === "expired" || (daysLeft != null && daysLeft < 0)
      ? "text-red-600"
      : daysLeft != null && daysLeft <= ENDS_SOON_DAYS
        ? "text-amber-600"
        : "";
  const maxEmployees = employeeUsage?.max ?? subscription.maxEmployees;
  const maxSubCompanies = subCompanyUsage?.max ?? subscription.maxSubCompanies;

  return (
    <div className="max-w-2xl border rounded-xl bg-white shadow-md overflow-hidden">
      {/* 1. Plan */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
          {subscription.status && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || "bg-gray-100 text-gray-800"}`}>
              {subscription.status}
            </span>
          )}
        </div>
        {tier && (
          <p className="mt-1 text-blue-100">
            {t("subscription.selector.upTo")} {tier.maxEmployees} {t("subscription.selector.employees")}
          </p>
        )}
      </div>

      <div className="p-8">
        {/* 2. What they pay */}
        <Section title={t("subscription.selector.price")}>
          <Row label={t(`subscription.selector.${isYearly ? "Yearly" : "Monthly"}`)}>
            <span dir="ltr">
              {currency} {Number(subscription.price || 0).toFixed(2)}
              {t(`subscription.selector.${isYearly ? "perYear" : "perMonth"}`)}
            </span>
          </Row>
          <Row label={t("subscription.selector.autoRenewal")}>
            {subscription.isAutoRenewal ? t("subscription.selector.on") : t("subscription.selector.off")}
          </Row>
        </Section>

        {/* 3. Dates */}
        <Section title={t("subscription.selector.dates")}>
          <Row label={t("form.startDate")}>{formatDate(subscription.startDate)}</Row>
          <Row label={t("form.endDate")}>
            <span className={endDateClass}>{formatDate(subscription.endDate)}</span>
          </Row>
          <Row label={t("subscription.selector.nextBilling")}>{formatDate(subscription.nextBillingDate)}</Row>
          {subscription.lastBillingDate && (
            <Row label={t("subscription.selector.lastBilling")}>{formatDate(subscription.lastBillingDate)}</Row>
          )}
        </Section>

        {/* 4. Usage against limits */}
        <Section title={t("subscription.selector.usage")}>
          <Usage label={t("subscription.selector.maxEmployees")} used={employeeUsage?.used ?? 0} max={maxEmployees || 0} />
          {maxSubCompanies > 0 && (
            <Usage label={t("subscription.selector.maxCompanies")} used={subCompanyUsage?.used ?? 0} max={maxSubCompanies} />
          )}
        </Section>

        {/* 5. Features */}
        {plan.features && (
          <Section title={t("subscription.selector.features")}>
            <Row label={t("subscription.selector.supportLevel")}>{plan.features.support || "—"}</Row>
            <Row label={t("subscription.selector.customizationOptions")}>
              {plan.features.customizationOptions ? t("subscription.selector.yes") : t("subscription.selector.no")}
            </Row>
          </Section>
        )}

        {/* 6. Footer */}
        <p className="mt-6 pt-4 border-t text-sm text-gray-500">{t("subscription.selector.contactToChange")}</p>
      </div>
    </div>
  );
};

export default CurrentSubscriptionCard;
